import { FlagRiskScaleLevel } from '../../../../graphQL';
import { colors } from '../../../../globalStyles';
import { SeverityTone } from './AssessmentFlags.types';

/**
 * Sort rank and display tone for the three levels the V2 scales fire at. Levels from different
 * scales describe different instruments, so no ordering spans them and none is written here: the
 * enum the endpoint types its flags on carries every scale's levels, and a rank over all of them
 * would assert a severity relation that does not exist.
 */
const WATCH_ELEVATED_SEVERE_META: Record<
  Extract<FlagRiskScaleLevel, 'Watch' | 'Elevated' | 'Severe'>,
  { rank: number; tone: SeverityTone }
> = {
  Watch: { rank: 0, tone: 'watch' },
  Elevated: { rank: 1, tone: 'elevated' },
  Severe: { rank: 2, tone: 'severe' },
};

// A level off this scale can still arrive, so both accessors answer for one rather than throwing.
// It ranks above every level that is ranked, because a severity-descending table has to put what it
// cannot place where someone will see it.
const UNRANKED_LEVEL_META = { rank: 100, tone: 'severe' } as const;

const metaFor = (severity: FlagRiskScaleLevel) =>
  WATCH_ELEVATED_SEVERE_META[severity as keyof typeof WATCH_ELEVATED_SEVERE_META] ??
  UNRANKED_LEVEL_META;

export const getSeverityTone = (severity: FlagRiskScaleLevel): SeverityTone =>
  metaFor(severity).tone;

export const getSeverityRank = (severity: FlagRiskScaleLevel): number => metaFor(severity).rank;

const TONE_DOT_COLOR: Record<SeverityTone, string> = {
  severe: colors.danger,
  watch: colors.secondary,
  elevated: colors.brandAlt,
};

export const getSeverityDotColor = (severity: FlagRiskScaleLevel): string =>
  TONE_DOT_COLOR[getSeverityTone(severity)];
